import { useState } from "react";
import { loginFailureMessage } from "@/lib/loginFailureMessage";
import { useAuth } from "@/hooks/use-auth";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, Mail, Shield } from "lucide-react";
import { OTPLogin } from "@/components/auth/OTPLogin";
import { LoginTurnstile } from "@/components/auth/LoginTurnstile";
import { loginRequestBody, responseRequiresCaptcha } from "@/lib/loginCaptcha";
import { isAppBuild } from "@/lib/net";
import { postLoginPath } from "@/lib/appReturnTo";

import { MFASetup } from "@/components/auth/MFASetup";
import { MFAVerification } from "@/components/auth/MFAVerification";
import { apiRequest } from "@/lib/queryClient";
import logoImage from "@assets/download.png";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'email' | 'whatsapp'>('whatsapp');
  const [mfaStep, setMfaStep] = useState<'none' | 'setup' | 'verify'>('none');
  const [mfaData, setMfaData] = useState<any>(null);
  // Login hardening (2026-09-30): Turnstile appears only after the server says captchaRequired.
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaUnavailable, setCaptchaUnavailable] = useState(false);
  const [captchaAttempt, setCaptchaAttempt] = useState(0);
  const { login, loginWithToken } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (captchaRequired && !captchaToken && !captchaUnavailable) {
      toast({ title: "One more step", description: "Please complete the check below.", variant: "destructive" });
      return;
    }
    setIsLoading(true);

    try {
      // Direct login attempt with MFA handling
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginRequestBody(email, password, { required: captchaRequired, token: captchaToken, unavailable: captchaUnavailable }))
      });

      if (!response.ok) {
        let errorData: any = {};
        try { errorData = await response.json(); } catch { /* non-JSON (proxy error page) */ }
        if (responseRequiresCaptcha(errorData)) setCaptchaRequired(true);
        const err: any = new Error(errorData.message || "Login failed");
        err.status = response.status;
        throw err;
      }

      const result = await response.json();

      if (result.requiresMFASetup) {
        // MFA setup required for admin/event manager
        setMfaStep('setup');
        setMfaData({ tempToken: result.tempToken, userRole: 'admin' });
        toast({
          title: "MFA Setup Required",
          description: result.message
        });
        return;
      }

      if (result.requiresMFA) {
        // MFA verification required
        setMfaStep('verify');
        setMfaData({ 
          email, 
          password,
          loginAttemptId: result.loginAttemptId,
          userId: result.userId || 2
        });
        toast({
          title: "Verification Required",
          description: result.message
        });
        return;
      }

      if (result.token && result.user) {
        // Normal login success
        loginWithToken(result.token, result.user);
        toast({
          title: "Login Successful",
          description: "Welcome to LA Mirage Wedding Planning System",
        });
        // L5: a client returns to the portal deep link they were bounced from.
        setLocation(postLoginPath({ role: result.user?.role, isApp: isAppBuild() }));
        return;
      }
    } catch (error: any) {
      if (error.message?.includes('MFA setup required') || error.message?.includes('MFA verification required')) {
        // Handle MFA requirements from login error
        const requiresSetup = error.message.includes('setup required');
        if (requiresSetup) {
          setMfaStep('setup');
          setMfaData({ userRole: 'admin' });
        } else {
          // Never invent a loginAttemptId — only the server issues one, after the
          // password step (audit 2026-09-27 H1). Ask the user to sign in again.
          toast({
            title: "Verification Required",
            description: "Please sign in again to receive a verification code.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Login Failed",
          description: loginFailureMessage(error?.status, error?.message),
          variant: "destructive",
        });
      }
    } finally {
      setIsLoading(false);
      if (captchaRequired || captchaToken) {
        // Tokens are single-use: drop it and let the widget issue a fresh one.
        setCaptchaToken(null);
        setCaptchaAttempt((n) => n + 1);
      }
    }
  };

  const handleMFASetupComplete = () => {
    setMfaStep('none');
    setMfaData(null);
    setLocation("/");
  };

  const handleMFAVerificationSuccess = async (token: string, user: any) => {
    try {
      if (!token || !user) throw new Error("Sign-in did not complete. Please sign in again.");
      // MFA verification successful - complete login
      loginWithToken(token, user);
      toast({
        title: "Login Successful",
        description: "Welcome to LA Mirage Wedding Planning System",
      });
      setMfaStep('none');
      setMfaData(null);
      setLocation(postLoginPath({ role: user?.role, isApp: isAppBuild() }));
    } catch (error: any) {
      toast({
        title: "Login Failed",
        description: error.message || "Authentication error",
        variant: "destructive",
      });
    }
  };

  const handleMFAError = (message: string) => {
    toast({
      title: "MFA Error",
      description: message,
      variant: "destructive",
    });
  };

  const handleOTPSuccess = (token: string, user: any) => {
    loginWithToken(token, user);
    // App: back to the deep link the launch was bounced from. Web (L5): the same, but
    // only for role=client and only for client-portal paths.
    setLocation(postLoginPath({ role: user?.role, isApp: isAppBuild() }));
  };

  const handleBackToEmailLogin = () => {
    setLoginMethod('email');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black p-4">
      <Card className="w-full max-w-md bg-black border-dark-yellow/30">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-dark-yellow to-dark-yellow rounded-2xl flex items-center justify-center">
            <img 
              src={logoImage} 
              alt="LA Mirage Logo" 
              className="w-12 h-12 object-contain"
            />
          </div>
          <div>
            <CardTitle className="text-2xl font-playfair text-dark-yellow">LA Mirage Koratty</CardTitle>
            <CardDescription className="text-white/70">Wedding Planning & CRM System</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {mfaStep === 'setup' ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <Shield className="h-5 w-5 text-dark-yellow" />
                <h3 className="text-lg font-semibold text-dark-yellow">MFA Setup Required</h3>
              </div>
              <MFASetup 
                onComplete={handleMFASetupComplete}
                userRole="admin"
                tempToken={mfaData?.tempToken}
              />
            </div>
          ) : mfaStep === 'verify' ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <Shield className="h-5 w-5 text-dark-yellow" />
                <h3 className="text-lg font-semibold text-dark-yellow">Enter Verification Code</h3>
              </div>
              <MFAVerification 
                userId={mfaData?.userId || 2}
                loginAttemptId={mfaData?.loginAttemptId}
                onSuccess={handleMFAVerificationSuccess}
                onError={handleMFAError}
              />
            </div>
          ) : isAppBuild() || loginMethod === 'whatsapp' ? (
            <div className="space-y-4">
              <OTPLogin 
                onSuccess={handleOTPSuccess}
                // Mobile app: OTP is the only sign-in (no email/password, no vendor login).
                onBack={isAppBuild() ? undefined : handleBackToEmailLogin}
              />
            </div>
          ) : (
            <>
              {/* Login Method Selection */}
              <div className="flex space-x-2 mb-6">
                <Button
                  type="button"
                  onClick={() => setLoginMethod('email')}
                  variant={loginMethod === 'email' ? 'default' : 'outline'}
                  className={`flex-1 ${
                    loginMethod === 'email' 
                      ? 'bg-dark-yellow text-black hover:bg-dark-yellow' 
                      : 'border-dark-yellow/30 text-dark-yellow hover:bg-dark-yellow/10'
                  }`}
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Email
                </Button>
                <Button
                  type="button"
                  onClick={() => setLoginMethod('whatsapp')}
                  variant={loginMethod === 'whatsapp' ? 'default' : 'outline'}
                  className={`flex-1 ${
                    loginMethod === 'whatsapp' 
                      ? 'bg-dark-yellow text-black hover:bg-dark-yellow' 
                      : 'border-dark-yellow/30 text-dark-yellow hover:bg-dark-yellow/10'
                  }`}
                >
                  <MessageSquare className="mr-2 h-4 w-4" />
                  WhatsApp
                </Button>
              </div>

              {/* Email Login Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-dark-yellow">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="bg-dark-yellow/10 border-dark-yellow/30 text-white placeholder:text-white/50 focus:border-dark-yellow"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-dark-yellow">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="bg-dark-yellow/10 border-dark-yellow/30 text-white placeholder:text-white/50 focus:border-dark-yellow"
                    required
                  />
                </div>
                {captchaRequired && (
                  <LoginTurnstile
                    onToken={setCaptchaToken}
                    onUnavailable={() => setCaptchaUnavailable(true)}
                    resetKey={captchaAttempt}
                  />
                )}
                <Button 
                  type="submit" 
                  className="w-full bg-dark-yellow text-black hover:bg-dark-yellow font-medium" 
                  disabled={isLoading}
                >
                  {isLoading ? "Signing in..." : "Sign In"}
                </Button>
              </form>

              <div className="mt-4 text-center space-y-2">
                <a 
                  href="/password-reset" 
                  className="text-sm text-blue-600 hover:text-blue-800 underline block"
                >
                  Forgot your password?
                </a>
                <div className="text-white/50 text-sm">or</div>
                <a 
                  href="/vendor-login" 
                  className="text-sm text-dark-yellow hover:text-dark-yellow/80 underline block"
                >
                  Vendor? Login here
                </a>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}